"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useQuery } from "@tanstack/react-query";
import { IoMdShare } from "react-icons/io";
import { usePathname } from "next/navigation";

import CustomImage from "@/components/shared/CustomImage";
import { Typography } from "@/components/shared/Typography";
import { getLabel } from "@/data/labels";
import { QUOTE_PAGE_RESULT_LIMIT } from "@/data/constants";
import { getImageUrl } from "@/utils/getImageUrl";
import SocialShare from "@/components/shared/SocialShare";
import { QuoteListType } from "@/types/quotes";
import { fetchQuotesList } from "@/services/quotesService";
import { getLocaleFromPathname } from "@/utils/getLocaleFromPath";

interface QuoteListSectionProps {
  initialData: {
    docs: QuoteListType[];
    totalDocs: number;
    limit: number;
    page: number;
  };
  lang: AppLocale;
}

export default function QuoteListSection({ initialData, lang }: QuoteListSectionProps) {
  const pathname = usePathname();
  const locale = getLocaleFromPathname(pathname) || "te-IN";
  const [articles, setArticles] = useState<QuoteListType[]>(initialData.docs || []);
  const [filters, setFilters] = useState({
    limit: QUOTE_PAGE_RESULT_LIMIT,
    page: 1,
  });

  const { data, isFetching } = useQuery({
    queryKey: ["fetchQuotesList", filters.page],
    enabled: filters.page > 1,
    queryFn: () => fetchQuotesList({ limit: filters?.limit, page: filters?.page, lang }),
  });

  const handleLoadMore = () => {
    setFilters(prev => ({ ...prev, page: prev.page + 1 }));
  };

  useEffect(() => {
    if (data) {
      setArticles(prev => [...prev, ...(data?.data?.docs || [])]);
    }
  }, [data]);

  // Determine if we should show the Load More button
  const showLoadMore =
    articles?.length > 0 &&
    ((filters.page === 1 && initialData.totalDocs > articles.length) ||
      (data && data?.data?.totalDocs > articles.length));

  return (
    <div>
      {articles?.map(({ image, title, slug, author, _id, position, quote }, index) => {
        return (
          <div
            key={index}
            className="flex flex-row items-start gap-6 pt-6 pb-6 border-b group first:pt-0"
          >
            <Link
              href={`/${locale}/articles-periodicals/${slug}`}
              className="w-[30%] min-w-[30%] md:w-[33%] md:min-w-[33%] overflow-hidden rounded-md aspect-[16/12] md:aspect-video"
              aria-label={`View article: ${title}`}
            >
              <CustomImage
                src={getImageUrl(image?.sizes?.ld?.filename || image?.filename)}
                alt={image?.alt || ""}
                width={400}
                height={200}
                className="object-cover w-full h-full img-zoom-hover"
              />
            </Link>
            <div className="w-full">
              <Link href={`/${locale}/articles-periodicals/${slug}`} aria-label={`View article: ${title}`}>
                <Typography
                  variant="h5"
                  language={lang}
                  className="line-clamp-3 md:line-clamp-2 font-semibold !text-base md:!text-[18px]"
                >
                  {title}
                </Typography>
              </Link>
              <div className="hidden gap-4 my-4 uppercase divide-x-2 divide-black md:flex dark:divide-white">
                <Typography language={lang} className="font-medium" variant="body2">
                  {author}
                </Typography>
                <Typography
                  className="pl-4 text-secondaryText dark:text-darkBgSecondaryColor"
                  variant="body2"
                >
                  {position}
                </Typography>
              </div>
              <div className="">
                <Typography variant="body1" language={lang} className="line-clamp-3 md:line-clamp-2 ">
                  {quote}
                </Typography>
              </div>

              <div className="flex items-center justify-between gap-4 mt-4">
                 {/* <span className="block text-[#858585] text-sm uppercase">
                  {formatDate(publishedAt, lang)}
                </span>  */}

                <div className="hidden md:block">
                  <SocialShare title={title} url={`/${locale}/articles-periodicals/${slug}`} />
                </div>
                <div className="md:hidden">
                  <SocialShare
                    title={title}
                    url={`/${locale}/articles-periodicals/${slug}`}
                    customButton={
                      <button className="flex items-center justify-center text-gray-600 dark:text-white" aria-label="Share">
                        <IoMdShare />
                      </button>
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        );
      })}

      {showLoadMore && (
        <div className="flex items-center justify-center mt-8">
          <button
            className="load-more-btn"
            onClick={handleLoadMore}
            disabled={isFetching}
            aria-label={getLabel(lang, "LOAD_MORE")}
          >
            {getLabel(lang, "LOAD_MORE")}
          </button>
        </div>
      )}
    </div>
  );
}
